/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useDateUtilities from "../../hooks/useDateUtilities";
import Loading from "../../layouts/Loading/Loading";
import TemporalSelector, { ITemporalSelectorProps, TemporalSelectorModes } from "../../layouts/TemporalSelector/TemporalSelector";
import DashboardDailyCovers from "./DashboardDailyCovers";
import DashboardDailyDocuments from "./DashboardDailyDocuments";
import DashboardDailySells, { DailyAggregateData } from "./DashboardDailySells";

import { IZReportListState } from "../../features/ZReports/slices/zReportListSlices";
import WarningModal from "../../layouts/Modal/WarningModal";
import style from "./dashboard.module.css";
import DashboardIstogramDailyCovers from "./DashboardIstogramDailyCovers";
import DashboardIstogramDailyDocuments from "./DashboardIstogramDailyDocuments";
import DashboardIstogramDailySells from "./DashboardIstogramDailySells";

interface IDashboardComponentProps {
	// zReportAggregates: IZReportAggregate[];
	// zReportAggregatesCompare?: IZReportAggregate[];
	// zReportComparePreviousMonthAggregates?: IZReportAggregate[];
	zReportList: IZReportListState;
	dates: Date[];
	lastZReportDate: Date;
	isError: boolean;
	onRefresh: (...args: any) => void;
}

export type IstogramData = {
	name: string;
	sells: number;
	documents: number;
	covers: number;
};

export interface DashboardDailyData {
	aggregates: DailyAggregateData[];
	gran_totals: {
		document_gran_total: number;
		document_gran_num: number;
		cover_gran_num: number;
		cover_gran_total: number;
	};
	gran_totals_compare: {
		document_gran_total_compare: number;
		document_gran_num_compare: number;
		cover_gran_num_compare: number;
	};
	istogramData: IstogramData[];
}

const dataInitialState: DashboardDailyData = {
	aggregates: [],
	gran_totals: {
		document_gran_total: 0,
		document_gran_num: 0,
		cover_gran_num: 0,
		cover_gran_total: 0
	},
	gran_totals_compare: {
		document_gran_total_compare: 0,
		document_gran_num_compare: 0,
		cover_gran_num_compare: 0
	},
	istogramData: []
};

const DashboardComponent: React.FC<ITemporalSelectorProps & IDashboardComponentProps> = (props) => {
	const [data, setData] = useState<DashboardDailyData>(dataInitialState);
	const [display, setDisplay] = useState<string>("");
	const [mode, setMode] = useState<TemporalSelectorModes>(TemporalSelectorModes.week);
	const [currentIstogramData, setCurrentIstogramData] = useState<IstogramData[]>([]);

	const { t } = useTranslation();

	const { formatDateToServerFormat, formatDateToLocale, shiftWeekFromDate } = useDateUtilities();

	const formatData = (): DashboardDailyData => {
		let data: DailyAggregateData[] = [];

		let document_gran_total = 0,
			document_gran_num = 0,
			cover_gran_num = 0,
			cover_gran_total = 0,
			document_gran_total_compare = 0,
			document_gran_num_compare = 0,
			cover_gran_num_compare = 0;

		props.dates.forEach((date) => {
			let document_total = 0,
				document_total_compare = 0,
				document_num = 0,
				document_num_compare = 0,
				cover_num = 0,
				cover_num_compare = 0,
				cover_total_amount = 0,
				cover_avg = 0,
				bar_avg = 0;
			const formattedDate = formatDateToServerFormat(date);

			const zReportAggregatesFound = props.zReportList.zReportAggregates?.filter((zRepAggr) => zRepAggr.start_date === formattedDate) ?? [];
			if (zReportAggregatesFound.length > 0) {
				zReportAggregatesFound.forEach((zReport) => {
					document_total += zReport.document_total || 0;
					document_num += zReport.document_num || 0;
					cover_num += zReport.cover_num || 0;
					cover_total_amount += zReport.cover_total_amount || 0;
					cover_avg += zReport.cover_avg || 0;
					bar_avg += zReport.bar_avg || 0;
				});
			}

			const weeksToShift = -Math.round(props.dates.length / 7);

			const zReportAggregatesCompareFound =
				props.zReportList?.zReportCompareAggregates?.filter(
					(zRepAggrComp) => zRepAggrComp.start_date === formatDateToServerFormat(shiftWeekFromDate(date, weeksToShift))
				) ?? [];

			if (zReportAggregatesCompareFound.length > 0) {
				zReportAggregatesCompareFound.forEach((zReport) => {
					document_total_compare += zReport.document_total || 0;
					document_num_compare += zReport.document_num || 0;
					cover_num_compare += zReport.cover_num || 0;
				});
			}

			const document_avg = document_total && document_num ? document_total / document_num : 0;
			const document_avg_compare = document_total_compare && document_num_compare ? document_total_compare / document_num_compare : 0;

			const translatedWeekDay = t("shared.calendar.days." + date.toLocaleDateString("en-En", { weekday: "long" }) + ".letter");

			document_gran_total += document_total;
			document_gran_num += document_num;
			cover_gran_num += cover_num;
			cover_gran_total += cover_gran_total;

			document_gran_total_compare += document_total_compare;
			document_gran_num_compare += document_num_compare;
			cover_gran_num_compare += cover_num_compare;

			data.push({
				date: formatDateToLocale(date),
				dateDay: translatedWeekDay,
				document_total,
				document_total_compare,
				document_num,
				document_num_compare,
				cover_num,
				cover_num_compare,
				cover_total_amount,
				cover_avg,
				bar_avg,
				document_avg: Math.round(document_avg * 100) / 100,
				document_avg_compare: Math.round(document_avg_compare * 100) / 100
			});
		});
		const isMonth = mode === TemporalSelectorModes.month;

		const istogramData: IstogramData[] = [];
		let currentSellTotal = 0;
		let currentDocTotal = 0;
		let currentCoverTotal = 0;
		let previousSellTotal = 0;
		let previousDocTotal = 0;
		let previousCoverTotal = 0;
		props.zReportList?.zReportAggregates.forEach((currentMonth) => {
			currentSellTotal += currentMonth.document_total ?? 0;
			currentDocTotal += currentMonth.document_num ?? 0;
			currentCoverTotal += currentMonth.cover_num ?? 0;
		});

		const currentLabel = isMonth ? t("shared.currentMonth") : t("shared.currentWeek");

		istogramData.push({
			name: currentLabel,
			sells: currentSellTotal,
			documents: currentDocTotal,
			covers: currentCoverTotal
		});

		if (isMonth) {
			props.zReportList?.zReportComparePreviousMonthAggregates?.forEach((prevMonth) => {
				previousSellTotal += prevMonth.document_total ?? 0;
				previousDocTotal += prevMonth.document_num ?? 0;
				previousCoverTotal += prevMonth.cover_num ?? 0;
			});

			istogramData.push({
				name: t("shared.lastMonth"),
				sells: +previousSellTotal.toFixed(2),
				documents: previousDocTotal,
				covers: previousCoverTotal
			});
		} else {
			props.zReportList?.zReportCompareAggregates?.forEach((prevWeek) => {
				previousSellTotal += prevWeek.document_total ?? 0;
				previousDocTotal += prevWeek.document_num ?? 0;
				previousCoverTotal += prevWeek.cover_num ?? 0;
			});

			istogramData.push({
				name: t("shared.lastWeek"),
				sells: +previousSellTotal.toFixed(2),
				documents: previousDocTotal,
				covers: previousCoverTotal
			});
		}

		return {
			aggregates: data,
			gran_totals: {
				document_gran_total: Math.ceil(document_gran_total * 100) / 100,
				document_gran_num,
				cover_gran_num,
				cover_gran_total: Math.ceil(cover_gran_total * 100) / 100
			},
			gran_totals_compare: {
				document_gran_total_compare: Math.ceil(document_gran_total_compare * 100) / 100,
				document_gran_num_compare: document_gran_num_compare,
				cover_gran_num_compare: cover_gran_num_compare
			},
			istogramData: istogramData
		};
	};

	useEffect(() => {
		const formattedData = formatData();
		setData(formattedData);
		setCurrentIstogramData([...formattedData.istogramData]);
	}, [props.zReportList]);

	const handleDisplayChange = (display: string) => {
		setDisplay(display);
	};

	const handleModeChange = (mode: TemporalSelectorModes): void => {
		setMode(mode);
		setData(dataInitialState);
	};

	const handleDataChange = (startDate: Date, stopDate: Date): void => {
		props.onDateChange(startDate, stopDate, mode);
		setData(dataInitialState);
	};

	const compareLegendLabel = useMemo(() => (mode === TemporalSelectorModes.week ? "shared.lastWeek" : "shared.lastMonth"), [mode]);

	return (
		<React.Fragment>
			<TemporalSelector
				onModeChange={handleModeChange}
				onDateChange={handleDataChange}
				currentDate={props.lastZReportDate}
				onDisplayChange={handleDisplayChange}
			/>
			{!props.zReportList.isLoading && data && data.aggregates.length > 0 ? (
				<React.Fragment>
					{data.istogramData.length > 0 && (
						<>
							<DashboardIstogramDailySells responsiveContainerClassName={style.responsiveContainer} mode={mode} data={currentIstogramData} />
							<DashboardIstogramDailyDocuments responsiveContainerClassName={style.responsiveContainer} mode={mode} data={currentIstogramData} />
							<DashboardIstogramDailyCovers responsiveContainerClassName={style.responsiveContainer} mode={mode} data={currentIstogramData} />
						</>
					)}
					<DashboardDailySells
						responsiveContainerClassName={style.responsiveContainer}
						mode={mode}
						compareLegendLabel={compareLegendLabel}
						data={data}
						reportCaption={display}
					/>
					<DashboardDailyDocuments
						responsiveContainerClassName={style.responsiveContainer}
						mode={mode}
						compareLegendLabel={compareLegendLabel}
						data={data}
						reportCaption={display}
					/>
					<DashboardDailyCovers
						responsiveContainerClassName={style.responsiveContainer}
						mode={mode}
						compareLegendLabel={compareLegendLabel}
						data={data}
						reportCaption={display}
					/>
				</React.Fragment>
			) : (
				<Loading />
			)}

			<WarningModal
				title={t("zreports.zreport.fetchingErrorTitle")}
				message={t("zreports.zreport.fetchingErrorMessage")}
				onOk={props.onRefresh}
				isOpen={props.isError}
			/>
		</React.Fragment>
	);
};

export default DashboardComponent;
